import { lazy, Suspense, useEffect, useState } from 'react';

import { Button, useToast } from '@chakra-ui/react';
import { HttpStatusCode } from 'axios';

import Loader from '@/components/Layouts/Loader';
import { COLORS, DEFAULT_PAGE_NUMBER, DEFAULT_PAGE_SIZE, DURATION_TOAST, FORMAT_ADD_DATE, FORMAT_UI_DATE } from '@/common/constants';

import './styles/CurrencyExchange.css';
import '../styles/ListPage.css';
import { ExchangeRate, getExchangeRateDateRange, getLatestExchangeRateFromJisdor } from '@/services/exchangeRateService';
import DatePicker from 'react-datepicker';
import { format, sub } from 'date-fns';

const TableCurrencyExchange = lazy(() => import('@/components/Tables/TableCurrencyExchange'));

const CurrencyExchange = () => {
    const toast = useToast();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const defaultStartDate = sub(today, { days: 15 })
    const defaultEndDate = sub(today, { days: 1 })

    const [currentPageNumber, setCurrentPageNumber] = useState(DEFAULT_PAGE_NUMBER);
    const [currentPageSize, setCurrentPageSize] = useState(DEFAULT_PAGE_SIZE);
    const [dataList, setDataList] = useState<ExchangeRate[]>([]);
    const [hasNextPage, setHasNextPage] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [totalPageNumber, setTotalPageNumber] = useState(1);
    const [startDate, setStartDate] = useState<Date>(defaultStartDate);
    const [endDate, setEndDate] = useState<Date>(defaultEndDate);

    const showErrorToast = (error: { status?: string; code?: string; message: string }) => {
        toast({
            title: error.status || 'Error',
            description: error.message,
            status: 'error',
            duration: DURATION_TOAST,
            isClosable: true
        });
    };

    const getData = async () => {
        setIsLoading(true);
        const start = format(startDate, FORMAT_ADD_DATE);
        const end = format(endDate, FORMAT_ADD_DATE);
        try {
            const response = await getExchangeRateDateRange(start, end, currentPageNumber, currentPageSize);
            if (response.status === HttpStatusCode.Ok && response.data) {
                const formatted = response.data.map((entry: {date: string, kurs: number}) => ({date: entry.date, value: entry.kurs}))
                setDataList(formatted);
                setTotalPageNumber(response.totalPage);
                setHasNextPage(response.hasNextPage);
            } else {
                showErrorToast(response);
            }
        } catch (error) {
            console.log(error);
            showErrorToast({ message: 'An error occurred while fetching data.' });
        } finally {
            setIsLoading(false);
        }
    };

    const refreshJisdor = async () => {
        setIsLoading(true);
        try {
            await getLatestExchangeRateFromJisdor();
            await getData();
        } catch (error) {
            console.log(error);
            showErrorToast({ message: 'An error occurred while fetching latest exchange rate.' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (endDate >= startDate){
            getData();
        }
    }, [currentPageSize, currentPageNumber, startDate, endDate]);

    return (
        <>
            <div className="container">
                <div className="table-filter-action-container">
                    <div className="table-filter-container">
                        <div style={{ display: 'flex', alignItems: 'flex-start', flexDirection: 'column', gap: '4px' }}>
                            <div style={{ display: 'flex', gap: '12px' }}>
                                <div>
                                    <label htmlFor='start-date' className='date-picker-label'>From</label>
                                    <div className="date-picker">
                                        <DatePicker
                                            name='start-date'
                                            onChange={(newStart) => { if(newStart){setStartDate(newStart)} }}
                                            selected={startDate}
                                            placeholderText='Start date'
                                            dateFormat={FORMAT_UI_DATE}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor='end-date' className='date-picker-label'>To</label>
                                    <div className="date-picker">
                                        <DatePicker
                                            name='end-date'
                                            onChange={(newEnd) => { if(newEnd){setEndDate(newEnd)} }}
                                            selected={endDate}
                                            placeholderText='End date'
                                            dateFormat={FORMAT_UI_DATE}
                                        />
                                    </div>
                                </div>
                            </div>
                            {(endDate < startDate) && <p className="date-picker-label date-error" style={{marginTop: 8}}>End date cannot be earlier than start date</p>}
                        </div>
             
                        <div className="table-filter-separator"/>
                        <Button
                            mt={0}
                            bgColor={COLORS.BUTTON.BACKGROUND}
                            color={COLORS.BUTTON.FOREGROUND}
                            isLoading={isLoading}
                            onClick={refreshJisdor}
                            title='Click here if the latest exchange rate is missing.'
                        >
                            Get Latest Exchange Rate
                        </Button>
                    </div>
                </div>
                <Suspense fallback={<Loader />}>
                    <TableCurrencyExchange
                        dataList={dataList}
                        isLoading={isLoading}
                        currentPageSize={currentPageSize}
                        handleChangePageSize={setCurrentPageSize}
                        handleChangePageNumber={setCurrentPageNumber}
                        currentPageNumber={currentPageNumber}
                        totalPageNumber={totalPageNumber}
                        hasNextPage={hasNextPage}
                    />
                </Suspense>
            </div>
        </>
    );
};

export default CurrencyExchange;
